/** @format */
import { Component, ElementRef, Input, OnChanges, OnInit, QueryList, SimpleChanges, ViewChildren, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import * as _ from 'lodash-es';
import { getLabelState } from '../../form/formUtils';

/**
 * An editor for a `LocalizedString` that works purely based on the `FormGroup` instance given as an input parameter.
 * The editor's initial value, available locales, and validation rules are all based on the controls contained in the
 * `formGroup`.
 */
@Component({
    selector: 'sis-localized-string-editor',
    templateUrl: './localized-string-editor.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class LocalizedStringEditorComponent implements OnInit, OnChanges {
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input('editorFormGroup') formGroup: FormGroup;
    @Input() label: string;
    /** If true, the label element will have the form validation status icons */
    @Input() showLabelIcon = true;
    @Input() name: string;
    @Input() selectedLocale: string;
    @Input() placeholder: string;
    @Input() useTextArea: boolean;
    /** Translation key for additional help-block */
    @Input() helpBlock?: string;
    @Input() helpBlockParams?: any;
    /** Translation key for additional guidance */
    @Input() guidance?: string;
    @Input() showEmptyValuesAsInvalid = false;
    @ViewChildren('input') inputs: QueryList<ElementRef>;

    currentLocale: string;

    ngOnInit() {
        this.currentLocale = this.selectedLocale || _.first(this.locales);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.selectedLocale) {
            this.currentLocale = changes.selectedLocale.currentValue || this.currentLocale || _.first(this.locales);
        }
    }

    get labelState(): string {
        return this.showLabelIcon ? getLabelState(this.formGroup) : '';
    }

    get locales(): string[] {
        return !_.isNil(this.formGroup) && !_.isEmpty(this.formGroup.controls) ? Object.keys(this.formGroup.controls) : [];
    }

    getControlFor(locale: string): FormControl {
        return !_.isNil(this.formGroup) ? (this.formGroup.get(locale) as FormControl) : null;
    }

    getLocalesWithError(errorName: string) {
        return !_.isNil(this.formGroup) && this.locales.filter(locale => _.has(this.formGroup.get(locale).errors, errorName));
    }

    getInputFieldId(locale: string) {
        return `languageInput_${this.name}_${locale}`;
    }

    getInvalidLangFields() {
        return _.filter(
            this.locales,
            locale =>
                (this.formGroup.touched && this.isInvalid(locale)) || (this.showEmptyValuesAsInvalid && !this.formGroup.value[locale]),
        );
    }

    focusLocaleInput(locale: string) {
        if (!!this.inputs) {
            const found = this.inputs
                .toArray()
                .map(elementRef => elementRef.nativeElement)
                .find(element => element.getAttribute('name') === locale);
            if (found) {
                found.focus();
            }
        }
    }

    isInvalid(locale: string): boolean {
        if (_.isNil(this.formGroup) || !this.formGroup.contains(locale)) {
            return false;
        }

        return (
            this.formGroup.get(locale).invalid || (locale === this.currentLocale && _.has(this.formGroup.errors, 'oneChildValueRequired'))
        );
    }

    isValidationErrorBlockVisible() {
        if (_.get(this, 'formGroup.updateOn') === 'change') {
            return (this.formGroup.touched || this.formGroup.dirty) && this.formGroup.invalid;
        }
        return this.formGroup.touched && this.formGroup.invalid;
    }
}
